// lib
import { unwrapResult } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useBeforeUnload } from "react-use";
import Form from "react-bootstrap/Form";

// styles & svg
import styles from "./AuthPage.module.css";
import { ReactComponent as DoctorsSvg } from "./doctors.svg";
import { ReactComponent as LogoSvg } from "../../app/assets/logo.svg";

// slice
import { logout, loginThunk, statusThunk, verifyMFAThunk } from "./userSlice";
import { AthelasButton } from "../../app/Atoms/AthelasButton/AthelasButton";

// The site key is intentionally public. Cloudflare site keys are meant to be
// embedded client-side.
const TURNSTILE_SITE_KEY = "0x4AAAAAAEMhILAYSDfcg4qR";
const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_ONLOAD_CALLBACK = "onloadTurnstileCallback";

export const AuthPage = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [OTP, setOTP] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);

  const turnstileContainer = useRef(null);
  const turnstileWidgetId = useRef(undefined);

  const errorMsg = useSelector((state) => state.user.errorMsg);
  const requires_mfa = useSelector((state) => state.user.requires_mfa);

  const dispatch = useDispatch();
  const history = useHistory();

  const onEmailChanged = (e) => setEmail(e.target.value);
  const onPasswordChanged = (e) => setPassword(e.target.value);
  const onOTPChanged = (e) => setOTP(e.target.value);

  const forScribble = props.forScribble || false;

  // reset state when refresh
  // so don't stuck on OTP page
  useBeforeUnload(() => dispatch(logout()));

  // The CAPTCHA only guards the credential step, so it is rendered (and torn
  // down) alongside the login form rather than the OTP form.
  useEffect(() => {
    if (requires_mfa) {
      return undefined;
    }

    const renderWidget = () => {
      if (turnstileWidgetId.current !== undefined) {
        return;
      }
      if (turnstileContainer.current && window.turnstile) {
        turnstileWidgetId.current = window.turnstile.render(
          turnstileContainer.current,
          {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(null),
          }
        );
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      // Turnstile invokes this global once the API is ready to render.
      window[TURNSTILE_ONLOAD_CALLBACK] = renderWidget;
      if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${TURNSTILE_ONLOAD_CALLBACK}&render=explicit`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    return () => {
      if (turnstileWidgetId.current !== undefined && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = undefined;
      }
    };
  }, [requires_mfa]);

  // Turnstile tokens are single-use, so a consumed one has to be replaced
  // before the user can retry.
  const resetTurnstile = () => {
    setCaptchaToken(null);
    if (turnstileWidgetId.current !== undefined && window.turnstile) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  };

  const onLoginClicked = async () => {
    if (email && password) {
      try {
        const resultAction = await dispatch(
          loginThunk({ email, password, captchaToken })
        );
        const loginResponse = unwrapResult(resultAction);

        // no OTP required nor activated
        if ("auth_token" in loginResponse) {
          const token = loginResponse.auth_token;
          dispatch(statusThunk({ token }));
          if (forScribble) {
            history.push("/scribble");
          } else {
            history.push("/home");
          }
        }
      } catch (err) {
        resetTurnstile();
        console.error("Failed to login: ", err);
      }
    }
  };

  const onVerifyOTPClicked = async () => {
    if (email && OTP) {
      try {
        const resultAction = await dispatch(verifyMFAThunk({ email, OTP }));
        const loginResponse = unwrapResult(resultAction);
        const token = loginResponse.auth_token;
        dispatch(statusThunk({ token }));
        if (forScribble) {
          history.push("/scribble");
        } else {
          history.push("/home");
        }
      } catch (err) {
        console.log("Failed to verify OTP");
      }
    }
  };

  const passwordFieldKeyDown = (event) => {
    if (event.key === "Enter") {
      // prevent form submission
      event.preventDefault();
      onLoginClicked();
    }
  };

  const OTPFieldKeyDown = (event) => {
    if (event.key === "Enter") {
      // prevent form submission
      event.preventDefault();
      onVerifyOTPClicked();
    }
  };

  const loginForm = (
    <>
      <Form.Group controlId="formBasicEmail" className={styles.loginFormGroup}>
        <Form.Label>Username</Form.Label>
        <Form.Control
          type="Username"
          placeholder="Enter username"
          value={email}
          onChange={onEmailChanged}
        />
      </Form.Group>

      <Form.Group controlId="formBasicPassword">
        <Form.Label>Password</Form.Label>
        <Form.Control
          type="password"
          placeholder="Password"
          value={password}
          onChange={onPasswordChanged}
          onKeyDown={passwordFieldKeyDown}
        />
      </Form.Group>

      <Form.Group>
        <Form.Text className="text-danger">
          <b>{errorMsg}</b>
        </Form.Text>
      </Form.Group>

      <Form.Group>
        <div ref={turnstileContainer} />
      </Form.Group>

      <div style={{ width: "50%" }}>
        <AthelasButton text="Sign In" size="small" onClick={onLoginClicked} />
      </div>

      <p style={{ marginTop: "16px" }}>
        <a href="password_reset">Forgot Password?</a>
      </p>
    </>
  );

  const optForm = (
    <>
      <Form.Group controlId="formBasicOTP" className={styles.loginFormGroup}>
        <Form.Label>Enter your OTP below</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter OTP"
          value={OTP}
          onChange={onOTPChanged}
          onKeyDown={OTPFieldKeyDown}
        />
      </Form.Group>

      <Form.Group>
        <Form.Text className="text-danger">
          <b>{errorMsg}</b>
        </Form.Text>
      </Form.Group>
      <div style={{ width: "50%" }}>
        <AthelasButton
          size="small"
          onClick={onVerifyOTPClicked}
          text="Verify OTP"
        />
      </div>
    </>
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.LeftImageContainer}>
        <DoctorsSvg />
      </div>
      <div className={styles.RightLoginContainer}>
        <div>
          <LogoSvg className={styles.LogoSVG} />
          <h3 className={styles.SignInHeader}>Sign In</h3>
          <Form>{requires_mfa ? optForm : loginForm}</Form>
        </div>
      </div>
    </div>
  );
};
